import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { SimDriverResult } from '../../services/api';
import { driverColors, lastName, useChartTokens, useWidth } from './chartKit';

interface Props {
  /** The drivers to draw (one = driver view with bands; several = matchup with range bars). */
  drivers: SimDriverResult[];
  /** The whole simulated field, for the "typical for this level" line (driver view only). */
  field?: SimDriverResult[];
  nSims?: number;
}

const ord = (n: number) => {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
  return `${n}${s}`;
};
const pct = (v: number) => `${Math.round(v * 100)}%`;
const span = ([a, b]: [number, number]) => (a === b ? ord(a) : `${ord(a)}–${ord(b)}`);

/** The narrowest run of positions around the most likely finish holding `target` of the simulated races. */
export const likelyRange = (dist: number[], target: number): [number, number] => {
  let lo = dist.indexOf(Math.max(...dist));
  let hi = lo;
  let mass = dist[lo];
  while (mass < target && (lo > 0 || hi < dist.length - 1)) {
    const left = lo > 0 ? dist[lo - 1] : -1;
    const right = hi < dist.length - 1 ? dist[hi + 1] : -1;
    if (right >= left) mass += dist[++hi];
    else mass += dist[--lo];
  }
  return [lo + 1, hi + 1];
};

const medianOf = (dist: number[]) => {
  let c = 0;
  for (let i = 0; i < dist.length; i++) if ((c += dist[i]) >= 0.5) return i + 1;
  return dist.length;
};

const PLOT_H = 190;
const ROW = 22;

/**
 * Finish odds ("boom or bust"): how often each finishing position came up in the simulated races. One driver gets
 * shaded bands for where most finishes land; several drivers get range bars under the chart.
 */
export const FinishOddsChart: React.FC<Props> = ({ drivers, field, nSims }) => {
  const tokens = useChartTokens();
  const { ref, width } = useWidth(280, 640);
  const [hover, setHover] = useState<number | null>(null);
  const shown = drivers.filter((d) => d.finishDist && d.finishDist.length > 0);
  const colors = driverColors(
    shown.map((d) => d.driver),
    tokens.series,
  );
  const single = shown.length === 1;
  // Matchup view: clicking a driver highlights their ranges (bands, filled curve, DNF share) and fades the others.
  const [focus, setFocus] = useState<string | null>(null);
  const toggleFocus = (driver: string) => setFocus((f) => (f === driver ? null : driver));

  // Typical curve for a driver at this level: the average of other drivers projected within 2.5 places.
  const typical = useMemo(() => {
    if (!single || !field) return null;
    const me = shown[0];
    const peers = field.filter(
      (d) => d.driver !== me.driver && d.finishDist?.length && Math.abs(d.avgFinish - me.avgFinish) <= 2.5,
    );
    if (peers.length === 0) return null;
    return me.finishDist!.map((_, i) => peers.reduce((t, p) => t + (p.finishDist![i] ?? 0), 0) / peers.length);
  }, [single, field, shown]);

  if (shown.length === 0) return null;

  const N = shown[0].finishDist!.length;
  const M = { top: 10, right: 12, bottom: 30, left: 40 };
  const H = PLOT_H + (single ? 0 : 16 + shown.length * ROW);
  const pw = width - M.left - M.right;
  const ph = PLOT_H - M.top - M.bottom;
  const ymax = Math.max(...shown.flatMap((d) => d.finishDist!), ...(typical ?? [])) * 1.1 || 1;
  const x = (pos: number) => M.left + ((pos - 1) / Math.max(1, N - 1)) * pw;
  const y = (p: number) => M.top + ph - (p / ymax) * ph;
  const line = (dist: number[]) => dist.map((p, i) => `${i ? 'L' : 'M'}${x(i + 1)},${y(p)}`).join(' ');
  const area = (dist: number[]) => `${line(dist)} L${x(N)},${y(0)} L${x(1)},${y(0)} Z`;
  const step = ymax > 0.3 ? 0.1 : 0.05;
  const yTicks = Array.from({ length: Math.floor(ymax / step) + 1 }, (_, i) => i * step);
  const xTicks = [1, 5, 10, 15, 20, 25, 30, 35, 40].filter((p) => p < N - 2).concat(N);
  const stats = shown.map((d) => ({
    r50: likelyRange(d.finishDist!, 0.5),
    r80: likelyRange(d.finishDist!, 0.8),
    med: medianOf(d.finishDist!),
  }));

  // The driver whose ranges are drawn: the only driver, or the one clicked in a matchup.
  const featured = single ? 0 : shown.findIndex((d) => d.driver === focus);
  const dimmed = (driver: string) => !single && focus !== null && focus !== driver;

  const summary = (d: SimDriverResult, i: number) => (
    <>
      usually <b>{span(stats[i].r50)}</b> · boom {pct(d.top5Prob)} · bust {pct(1 - d.top20Prob)} ({pct(d.dnfRate)} DNF)
    </>
  );

  return (
    <Box ref={ref} sx={{ position: 'relative', width: '100%' }}>
      <svg
        width={width}
        height={H}
        role="img"
        aria-label={`Boom or bust: finish odds for ${shown.map((d) => d.driver).join(', ')}`}
        style={{ display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          if (e.clientY - r.top > PLOT_H) return setHover(null);
          const pos = Math.round(((e.clientX - r.left - M.left) / pw) * (N - 1)) + 1;
          setHover(pos >= 1 && pos <= N ? pos : null);
        }}
      >
        {featured >= 0 &&
          [
            { r: stats[featured].r80, o: 0.1 },
            { r: stats[featured].r50, o: 0.18 },
          ].map((b, i) => {
            const x0 = x(Math.max(1, b.r[0] - 0.5));
            const x1 = x(Math.min(N, b.r[1] + 0.5));
            return (
              <rect
                key={i}
                x={x0}
                width={x1 - x0}
                y={M.top}
                height={ph}
                fill={colors.get(shown[featured].driver)}
                fillOpacity={b.o}
              />
            );
          })}
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={M.left} x2={M.left + pw} y1={y(v)} y2={y(v)} stroke={tokens.grid} />
            <text x={M.left - 6} y={y(v)} dy="0.32em" textAnchor="end" fontSize={11} fill={tokens.axisText}>
              {pct(v)}
            </text>
          </g>
        ))}
        {xTicks.map((p) => (
          <text key={p} x={x(p)} y={PLOT_H - 10} textAnchor="middle" fontSize={11} fill={tokens.axisText}>
            {ord(p)}
          </text>
        ))}
        {typical && (
          <path
            d={line(typical)}
            fill="none"
            stroke={tokens.axisText}
            strokeWidth={1.5}
            strokeDasharray="5 4"
            strokeOpacity={0.8}
          />
        )}
        {/* Faded drivers first, so the highlighted one draws on top. */}
        {[...shown]
          .sort((a, b) => Number(!dimmed(a.driver)) - Number(!dimmed(b.driver)))
          .map((d) => {
            const c = colors.get(d.driver);
            const isFeatured = featured >= 0 && shown[featured].driver === d.driver;
            return (
              <g key={d.driver} opacity={dimmed(d.driver) ? 0.3 : 1}>
                {isFeatured && <path d={area(d.finishDist!)} fill={c} fillOpacity={0.12} />}
                {isFeatured && d.dnfDist && <path d={area(d.dnfDist)} fill={tokens.axisText} fillOpacity={0.35} />}
                <path
                  d={line(d.finishDist!)}
                  fill="none"
                  stroke={c}
                  strokeWidth={isFeatured && !single ? 2.5 : 2}
                  strokeLinejoin="round"
                />
                {!single && (
                  // Wider invisible stroke: an easier click target than the 2px line.
                  <path
                    d={line(d.finishDist!)}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={12}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleFocus(d.driver)}
                  />
                )}
              </g>
            );
          })}
        {!single &&
          shown.map((d, i) => {
            const cy = PLOT_H + 12 + i * ROW;
            const c = colors.get(d.driver);
            const s = stats[i];
            return (
              <g
                key={`range-${d.driver}`}
                opacity={dimmed(d.driver) ? 0.3 : 1}
                style={{ cursor: 'pointer' }}
                onClick={() => toggleFocus(d.driver)}
              >
                <rect x={M.left} width={pw} y={cy - ROW / 2} height={ROW} fill="transparent" />
                <line
                  x1={x(s.r80[0])}
                  x2={x(s.r80[1])}
                  y1={cy}
                  y2={cy}
                  stroke={c}
                  strokeWidth={2}
                  strokeLinecap="round"
                />
                <rect
                  x={x(s.r50[0]) - 4}
                  width={x(s.r50[1]) - x(s.r50[0]) + 8}
                  y={cy - 5}
                  height={10}
                  rx={5}
                  fill={c}
                />
                <circle cx={x(s.med)} cy={cy} r={4.5} fill={tokens.surface} stroke={c} strokeWidth={2} />
                <text
                  x={Math.min(x(s.r80[1]) + 10, M.left + pw - 60)}
                  y={cy}
                  dy="0.32em"
                  fontSize={12}
                  fill={tokens.text}
                  fontWeight={focus === d.driver ? 700 : 400}
                >
                  {lastName(d.driver)}
                </text>
              </g>
            );
          })}
        {hover && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={M.top} y2={M.top + ph} stroke={tokens.axisText} strokeOpacity={0.5} />
            {shown.map((d) => (
              <circle
                key={d.driver}
                cx={x(hover)}
                cy={y(d.finishDist![hover - 1])}
                r={4.5}
                fill={colors.get(d.driver)}
                stroke={tokens.surface}
                strokeWidth={2}
              />
            ))}
          </g>
        )}
      </svg>
      {hover && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: Math.min(Math.max(x(hover) + 12, 8), width - 230),
            width: 220,
            pointerEvents: 'none',
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            boxShadow: 3,
            p: 1,
            zIndex: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Finishing {ord(hover)}
          </Typography>
          {shown.map((d) => (
            <Stack key={d.driver} direction="row" spacing={0.75} alignItems="center">
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.get(d.driver), flexShrink: 0 }} />
              <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {d.driver}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {(d.finishDist![hover - 1] * 100).toFixed(1)}%
              </Typography>
            </Stack>
          ))}
        </Box>
      )}

      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {shown.map((d, i) => (
          <Stack
            key={d.driver}
            direction="row"
            spacing={0.75}
            alignItems="flex-start"
            {...(!single && {
              component: 'button',
              type: 'button',
              onClick: () => toggleFocus(d.driver),
              'aria-pressed': focus === d.driver,
            })}
            sx={{
              opacity: dimmed(d.driver) ? 0.5 : 1,
              ...(!single && {
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
                bgcolor: focus === d.driver ? 'action.selected' : 'transparent',
                border: 0,
                borderRadius: 1,
                px: 0.5,
                mx: -0.5,
                py: 0.25,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }),
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: colors.get(d.driver),
                mt: '5px',
                flexShrink: 0,
              }}
            />
            <Typography variant="body2">
              {single ? '' : `${d.driver}: `}
              {summary(d, i)}
            </Typography>
          </Stack>
        ))}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {featured >= 0 && (
            <>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: 14,
                    height: 10,
                    borderRadius: '2px',
                    bgcolor: colors.get(shown[featured].driver),
                    opacity: 0.45,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  usually (half the time)
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: 14,
                    height: 10,
                    borderRadius: '2px',
                    bgcolor: colors.get(shown[featured].driver),
                    opacity: 0.22,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  8 in 10
                </Typography>
              </Stack>
              {shown[featured].dnfDist && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 14, height: 10, borderRadius: '2px', bgcolor: tokens.axisText, opacity: 0.45 }} />
                  <Typography variant="caption" color="text.secondary">
                    DNF
                  </Typography>
                </Stack>
              )}
              {typical && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <svg width={22} height={6} aria-hidden>
                    <line
                      x1={0}
                      x2={22}
                      y1={3}
                      y2={3}
                      stroke={tokens.axisText}
                      strokeWidth={1.5}
                      strokeDasharray="5 4"
                    />
                  </svg>
                  <Typography variant="caption" color="text.secondary">
                    typical for drivers projected near {ord(Math.round(shown[0].avgFinish))}
                  </Typography>
                </Stack>
              )}
            </>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          How often each finishing position came up in {nSims ? nSims.toLocaleString() : 'the'} simulated races.{' '}
          {single
            ? 'Usually = where half the finishes land.'
            : 'Bars: thick = where half the finishes land, thin = 8 in 10, circle = median. Click a driver to highlight their ranges.'}{' '}
          Boom = top 5; bust = 21st or worse.
        </Typography>
      </Stack>
    </Box>
  );
};
